import { useState } from 'react';
import { Popover } from '@localagent/ui/components/popover';
import { Tooltip } from '@localagent/ui/components/tooltip';
import {
  ALL_FILTER,
  SESSION_GROUPING_LABELS,
  SESSION_GROUPINGS,
  type SessionGoalOption,
  type SessionGrouping,
  type SessionListSort,
  type SessionListStatusFilter,
} from '@localagent/sdk';
import { formatPersonaName } from '../../utils/persona-display';

export interface SessionWorkspaceOption {
  id: string;
  label: string;
}

interface SessionListToolbarProps {
  grouping: SessionGrouping;
  onGroupingChange: (grouping: SessionGrouping) => void;
  roleFilter: string;
  roleOptions: string[];
  onRoleFilterChange: (role: string) => void;
  goalFilter: string;
  goalOptions: SessionGoalOption[];
  onGoalFilterChange: (goalId: string) => void;
  statusFilter: SessionListStatusFilter;
  onStatusFilterChange: (status: SessionListStatusFilter) => void;
  workspaceFilter: string;
  workspaceOptions: SessionWorkspaceOption[];
  onWorkspaceFilterChange: (workspaceId: string) => void;
  sort: SessionListSort;
  onSortChange: (sort: SessionListSort) => void;
  showEmptyGroups: boolean;
  onShowEmptyGroupsChange: (show: boolean) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  searchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
  onNewChat: () => void;
  selecting: boolean;
  onSelectingChange: (selecting: boolean) => void;
}

type FilterPanel = 'root' | 'status' | 'workspace' | 'grouping' | 'sort' | 'role' | 'goal';

const SORT_LABELS: Record<SessionListSort, string> = {
  last_activity: 'Last activity',
  created: 'Created',
  name: 'Name',
};

const iconButtonClass =
  'rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

function IconSearch() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function IconFilters() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" x2="20" y1="8" y2="8" />
      <line x1="4" x2="20" y1="16" y2="16" />
      <line x1="9" x2="9" y1="5" y2="11" />
      <line x1="15" x2="15" y1="13" y2="19" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}

function IconSelect() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 6h11" />
      <path d="M10 12h11" />
      <path d="M10 18h11" />
      <path d="m3 6 1 1 2-2" />
      <path d="m3 12 1 1 2-2" />
      <path d="m3 18 1 1 2-2" />
    </svg>
  );
}

function rowButtonClass(active?: boolean) {
  return `flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-caption ${
    active ? 'bg-accent' : 'hover:bg-accent'
  }`;
}

export function SessionListToolbar({
  grouping,
  onGroupingChange,
  roleFilter,
  roleOptions,
  onRoleFilterChange,
  goalFilter,
  goalOptions,
  onGoalFilterChange,
  statusFilter,
  onStatusFilterChange,
  workspaceFilter,
  workspaceOptions,
  onWorkspaceFilterChange,
  sort,
  onSortChange,
  showEmptyGroups,
  onShowEmptyGroupsChange,
  searchQuery,
  onSearchQueryChange,
  searchOpen,
  onSearchOpenChange,
  onNewChat,
  selecting,
  onSelectingChange,
}: SessionListToolbarProps) {
  const [panel, setPanel] = useState<FilterPanel>('root');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const roles =
    roleFilter !== ALL_FILTER && !roleOptions.includes(roleFilter)
      ? [...roleOptions, roleFilter]
      : roleOptions;
  const goals =
    goalFilter !== ALL_FILTER && !goalOptions.some((goal) => goal.id === goalFilter)
      ? [...goalOptions, { id: goalFilter, label: 'Selected goal' }]
      : goalOptions;

  const closeFilters = (open: boolean) => {
    setFiltersOpen(open);
    if (!open) setPanel('root');
  };

  return (
    <div className="flex flex-col gap-1.5 px-3 pb-2 pt-2" data-testid="session-list-toolbar">
      <div className="flex items-center justify-between gap-1">
        <h2 className="text-label font-semibold text-muted-foreground">CHATS</h2>
        <div className="flex items-center gap-0.5">
        <Tooltip label="New chat">
          <button
            type="button"
            className={iconButtonClass}
            onClick={onNewChat}
            data-testid="new-chat-button"
            aria-label="New chat"
          >
            <IconPlus />
          </button>
        </Tooltip>
        <Tooltip label="Search">
          <button
            type="button"
            className={iconButtonClass}
            onClick={() => onSearchOpenChange(!searchOpen)}
            data-testid="session-list-search-toggle"
            aria-pressed={searchOpen}
            aria-label="Search sessions"
          >
            <IconSearch />
          </button>
        </Tooltip>
        <Popover
          open={filtersOpen}
          onOpenChange={closeFilters}
          align="start"
          aria-label="Session list filters"
          triggerClassName={iconButtonClass}
          contentClassName="w-64 p-1"
          trigger={
            <span data-testid="session-list-filters" className="inline-flex">
              <Tooltip label="Filters">
                <span className="inline-flex" aria-label="Filters">
                  <IconFilters />
                </span>
              </Tooltip>
            </span>
          }
        >
          {panel === 'root' && (
            <div className="flex flex-col">
              <button
                type="button"
                className={rowButtonClass()}
                data-testid="session-list-status"
                data-value={statusFilter}
                onClick={() => setPanel('status')}
              >
                <span>Status</span>
                <span className="text-muted-foreground">{statusFilter === 'active' ? 'Active' : 'All'}</span>
              </button>
              <button
                type="button"
                className={rowButtonClass()}
                data-testid="session-list-workspace"
                data-value={workspaceFilter}
                onClick={() => setPanel('workspace')}
              >
                <span>Workspace</span>
                <span className="text-muted-foreground">
                  {workspaceFilter === ALL_FILTER
                    ? 'All'
                    : (workspaceOptions.find((w) => w.id === workspaceFilter)?.label ?? 'Selected')}
                </span>
              </button>
              <button
                type="button"
                className={rowButtonClass()}
                data-testid="session-list-grouping"
                data-value={grouping}
                onClick={() => setPanel('grouping')}
              >
                <span>Group by</span>
                <span className="text-muted-foreground">{SESSION_GROUPING_LABELS[grouping]}</span>
              </button>
              <button
                type="button"
                className={rowButtonClass()}
                data-testid="session-list-sort"
                data-value={sort}
                onClick={() => setPanel('sort')}
              >
                <span>Sort</span>
                <span className="text-muted-foreground">{SORT_LABELS[sort]}</span>
              </button>
              <button
                type="button"
                className={rowButtonClass(showEmptyGroups)}
                data-testid="session-list-empty-groups"
                aria-pressed={showEmptyGroups}
                onClick={() => onShowEmptyGroupsChange(!showEmptyGroups)}
              >
                Show empty groups
              </button>
              <button
                type="button"
                className={rowButtonClass()}
                data-testid="session-list-filter-role"
                data-value={roleFilter}
                onClick={() => setPanel('role')}
              >
                <span>Role</span>
                <span className="text-muted-foreground">
                  {roleFilter === ALL_FILTER ? 'All roles' : formatPersonaName(roleFilter)}
                </span>
              </button>
              <button
                type="button"
                className={rowButtonClass()}
                data-testid="session-list-filter-goal"
                data-value={goalFilter}
                onClick={() => setPanel('goal')}
              >
                <span>Goal</span>
                <span className="text-muted-foreground">
                  {goalFilter === ALL_FILTER
                    ? 'All goals'
                    : (goalOptions.find((g) => g.id === goalFilter)?.label ?? 'Selected goal')}
                </span>
              </button>
            </div>
          )}
          {panel === 'status' && (
            <OptionList
              onBack={() => setPanel('root')}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
              ]}
              selected={statusFilter}
              testId="session-list-status"
              onSelect={(value) => {
                onStatusFilterChange(value as SessionListStatusFilter);
                setPanel('root');
              }}
            />
          )}
          {panel === 'workspace' && (
            <OptionList
              onBack={() => setPanel('root')}
              options={[
                { value: ALL_FILTER, label: 'All workspaces' },
                ...workspaceOptions.map((workspace) => ({
                  value: workspace.id,
                  label: workspace.label,
                })),
              ]}
              selected={workspaceFilter}
              testId="session-list-workspace"
              onSelect={(value) => {
                onWorkspaceFilterChange(value);
                setPanel('root');
              }}
            />
          )}
          {panel === 'grouping' && (
            <OptionList
              onBack={() => setPanel('root')}
              options={SESSION_GROUPINGS.map((option) => ({
                value: option,
                label: SESSION_GROUPING_LABELS[option],
              }))}
              selected={grouping}
              testId="session-list-grouping"
              onSelect={(value) => {
                onGroupingChange(value as SessionGrouping);
                setPanel('root');
              }}
            />
          )}
          {panel === 'sort' && (
            <OptionList
              onBack={() => setPanel('root')}
              options={(Object.keys(SORT_LABELS) as SessionListSort[]).map((value) => ({
                value,
                label: SORT_LABELS[value],
              }))}
              selected={sort}
              testId="session-list-sort"
              onSelect={(value) => {
                onSortChange(value as SessionListSort);
                setPanel('root');
              }}
            />
          )}
          {panel === 'role' && (
            <OptionList
              onBack={() => setPanel('root')}
              options={[
                { value: ALL_FILTER, label: 'All roles' },
                ...roles.map((role) => ({ value: role, label: formatPersonaName(role) })),
              ]}
              selected={roleFilter}
              testId="session-list-filter-role"
              onSelect={(value) => {
                onRoleFilterChange(value);
                setPanel('root');
              }}
            />
          )}
          {panel === 'goal' && (
            <OptionList
              onBack={() => setPanel('root')}
              options={[
                { value: ALL_FILTER, label: 'All goals' },
                ...goals.map((goal) => ({ value: goal.id, label: goal.label })),
              ]}
              selected={goalFilter}
              testId="session-list-filter-goal"
              onSelect={(value) => {
                onGoalFilterChange(value);
                setPanel('root');
              }}
            />
          )}
        </Popover>
        <Tooltip label="Bulk actions">
          <button
            type="button"
            className={`${iconButtonClass}${selecting ? ' bg-accent text-foreground' : ''}`}
            onClick={() => onSelectingChange(!selecting)}
            data-testid="session-list-bulk-mode"
            aria-pressed={selecting}
            aria-label="Bulk actions"
          >
            <IconSelect />
          </button>
        </Tooltip>
        </div>
      </div>
      {searchOpen && (
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder="Search sessions"
          data-testid="session-list-search"
          className="w-full rounded bg-muted px-2 py-1 text-caption"
          autoFocus
        />
      )}
    </div>
  );
}

function OptionList({
  options,
  selected,
  testId,
  onSelect,
  onBack,
}: {
  options: { value: string; label: string }[];
  selected: string;
  testId: string;
  onSelect: (value: string) => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col">
      <button type="button" className={rowButtonClass()} onClick={onBack}>
        Back
      </button>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={rowButtonClass(option.value === selected)}
          data-testid={testId}
          data-value={option.value}
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
